<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import { onMount, onDestroy } from 'svelte';

    import { getLabel } from './utils/getLabel';
    import { getTimeDifferenceByCycle } from './utils/getTimeDifferenceByCycle';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';

    import type { RepeatCycle } from '$lib/types/Components/Countdown/CountdownContent';
    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let box: BoxType;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let datetime: string | null = null;
    export let color = 'black';
    export let separatorColor: string | undefined = undefined;
    export let gapClass: string | undefined = undefined;
    export let showSeconds = true;
    export let showLabels = true;
    export let cycle: RepeatCycle = 'weekly';
    export let language: Language = 'en';

    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    let timer: ReturnType<typeof setInterval>;

    const updateCountdown = () => {
        if (!datetime) {
            return;
        }

        const diffInSeconds = getTimeDifferenceByCycle(cycle, datetime);

        if (diffInSeconds <= 0 && cycle === 'fixed') {
            clearInterval(timer);
            days = hours = minutes = seconds = 0;

            return;
        }

        days = Math.floor(diffInSeconds / 86400);
        hours = Math.floor((diffInSeconds % 86400) / 3600);
        minutes = Math.floor((diffInSeconds % 3600) / 60);
        seconds = diffInSeconds % 60;
    };

    onMount(() => {
        updateCountdown();
        timer = setInterval(updateCountdown, 1000);
    });

    onDestroy(() => {
        if (timer) {
            clearInterval(timer);
        }
    });

    const numClassName = 'fun-font-semibold fun-text-3xl fun-tabular-nums lg:fun-text-4xl';
    const labelClassName = 'fun-text-xs fun-opacity-60 lg:fun-text-sm';
    const wrapperClassName =
        'fun-flex fun-min-w-14 fun-flex-col fun-items-center fun-justify-center';
</script>

<Box {id} {...getBoxAttributes({ box })} {transitionDelay} {transitionEffect}>
    <div
        style:color
        class={cn(
            'fun-flex fun-items-center fun-justify-center fun-text-center',
            gapClass ?? 'fun-gap-5',
        )}
    >
        <div class={wrapperClassName}>
            <div class={numClassName}>{days.toString().padStart(2, '0')}</div>
            {#if showLabels}
                <span class={labelClassName}>{getLabel('Days', language)} </span>
            {/if}
        </div>

        <div
            class={cn('fun-h-6 fun-w-px', { 'fun-opacity-10': !separatorColor })}
            style:background-color={separatorColor ?? 'currentColor'}
        />

        <div class={wrapperClassName}>
            <div class={numClassName}>{hours.toString().padStart(2, '0')}</div>
            {#if showLabels}
                <span class={labelClassName}>{getLabel('Hours', language)} </span>
            {/if}
        </div>

        <div
            class={cn('fun-h-6 fun-w-px', { 'fun-opacity-10': !separatorColor })}
            style:background-color={separatorColor ?? 'currentColor'}
        />

        <div class={wrapperClassName}>
            <div class={numClassName}>{minutes.toString().padStart(2, '0')}</div>
            {#if showLabels}
                <span class={labelClassName}>{getLabel('Minutes', language)} </span>
            {/if}
        </div>

        {#if showSeconds}
            <div
                class={cn('fun-h-6 fun-w-px', { 'fun-opacity-10': !separatorColor })}
                style:background-color={separatorColor ?? 'currentColor'}
            />

            <div class={wrapperClassName}>
                <div class={numClassName}>{seconds.toString().padStart(2, '0')}</div>
                {#if showLabels}
                    <span class={labelClassName}>{getLabel('Seconds', language)} </span>
                {/if}
            </div>
        {/if}
    </div>
</Box>
